import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, AlertTriangle, Clock, Palette, Database, X, Info, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { loadTimeSettings, saveTimeSettings, formatHour, TimeSettings } from '@/utils/timeSettings';
import { loadDurationSettings, saveDurationSettings, BASE_DURATION_OPTIONS, DEFAULT_DURATION_SETTINGS, DurationSettings } from '@/utils/durationSettings';
interface SettingsProps {
  onResetData: () => void;
  todos?: any[];
  defaultOpen?: boolean;
  defaultSection?: string;
  onClose?: () => void;
  
  onLevelTrackerVisibilityChange?: (visible: boolean) => void;
  onMissionsVisibilityChange?: (layoutMode: string) => void;
  onGeneralMissionVisibilityChange?: (visible: boolean) => void;
}
export default function Settings({
  onResetData,
  todos = [],
  defaultOpen = false,
  defaultSection,
  onClose,
  
  onLevelTrackerVisibilityChange,
  onMissionsVisibilityChange,
  onGeneralMissionVisibilityChange
}: SettingsProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showReset, setShowReset] = useState(false);
  const [timeSettings, setTimeSettings] = useState<TimeSettings>(loadTimeSettings());
  const [durationSettings, setDurationSettings] = useState<DurationSettings>(loadDurationSettings());
  const [selectedSetting, setSelectedSetting] = useState<string | null>(defaultSection || null);
  const [showTopCards, setShowTopCards] = useState(() => {
    return localStorage.getItem('hideTopCards') !== 'true';
  });
  const [showMissions, setShowMissions] = useState(() => {
    return localStorage.getItem('showMissions') === 'true';
  });
  const [showGeneralMission, setShowGeneralMission] = useState(() => {
    return localStorage.getItem('hideGeneralMission') !== 'true';
  });
  useEffect(() => {
    saveTimeSettings(timeSettings);
  }, [timeSettings]);

  useEffect(() => {
    saveDurationSettings(durationSettings);
  }, [durationSettings]);

  // Reload settings when dialog opens to ensure current values are shown
  useEffect(() => {
    if (isOpen) {
      setTimeSettings(loadTimeSettings());
      setDurationSettings(loadDurationSettings());
    }
  }, [isOpen]);

  // Sync internal state with defaultOpen prop changes
  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
  }, [defaultOpen]);

  // Handle visibility changes

  const handleTopCardsVisibilityChange = (visible: boolean) => {
    setShowTopCards(visible);
    localStorage.setItem('hideTopCards', (!visible).toString());
    if (onLevelTrackerVisibilityChange) {
      onLevelTrackerVisibilityChange(visible);
    }
  };

  const handleMissionsVisibilityChange = (value: string) => {
    setShowMissions(value === 'missions' || value === 'missions-full');
    localStorage.setItem('layoutMode', value);
    if (onMissionsVisibilityChange) {
      onMissionsVisibilityChange(value);
    }
  };

  const handleGeneralMissionVisibilityChange = (visible: boolean) => {
    setShowGeneralMission(visible);
    localStorage.setItem('hideGeneralMission', (!visible).toString());
    if (onGeneralMissionVisibilityChange) {
      onGeneralMissionVisibilityChange(visible);
    }
  };

  const handleResetData = () => {
    onResetData();
    
    // Reset local state to defaults
    
    setShowTopCards(true);
    setShowMissions(false);
    setShowGeneralMission(true);
    setTimeSettings(loadTimeSettings()); // This will load defaults since localStorage was cleared
    setDurationSettings(DEFAULT_DURATION_SETTINGS);
    saveDurationSettings(DEFAULT_DURATION_SETTINGS);
    
    // Call visibility change callbacks to sync with parent
    if (onLevelTrackerVisibilityChange) {
      onLevelTrackerVisibilityChange(true);
    }
    if (onMissionsVisibilityChange) {
      onMissionsVisibilityChange('standard');
    }
    if (onGeneralMissionVisibilityChange) {
      onGeneralMissionVisibilityChange(true);
    }
    
    setShowReset(false);
    toast({
      title: "Data Reset",
      description: "All todos, progress, and settings have been reset"
    });
  };
  const handleTimeSettingChange = (key: keyof TimeSettings, value: string) => {
    const hour = parseInt(value);
    const newSettings = {
      ...timeSettings,
      [key]: hour
    };

    // Validate that end of day is after reset time
    if (newSettings.endOfDayHour <= newSettings.resetHour) {
      toast({
        title: "Invalid Time Setting",
        description: "End of day must be after the daily reset time",
        variant: "destructive"
      });
      return;
    }
    setTimeSettings(newSettings);
    toast({
      title: "Time Settings Updated",
      description: `${key === 'endOfDayHour' ? 'End of day' : 'Reset time'} changed to ${formatHour(hour)}`
    });
  };

  const handleDurationSettingChange = (slotIndex: number, minutes: number) => {
    const newDurations = [...durationSettings.primaryDurations];
    
    // Check for duplicates
    if (newDurations.includes(minutes) && newDurations[slotIndex] !== minutes) {
      toast({
        title: "Duplicate Duration",
        description: "Each duration can only be selected once",
        variant: "destructive"
      });
      return;
    }
    
    newDurations[slotIndex] = minutes;
    const updatedSettings = { ...durationSettings, primaryDurations: newDurations };
    setDurationSettings(updatedSettings);
  };

  const handleDefaultDurationChange = (minutes: number) => {
    const updatedSettings = { ...durationSettings, defaultDuration: minutes };
    setDurationSettings(updatedSettings);
  };

  // Generate hour options (0-23)
  const hourOptions = Array.from({
    length: 24
  }, (_, i) => ({
    value: i.toString(),
    label: formatHour(i)
  }));
  const settingsOptions = [{
    id: 'appearance',
    label: 'Appearance',
    icon: Palette
  }, {
    id: 'time',
    label: 'Time',
    icon: Clock
  }, {
    id: 'durations',
    label: 'Durations',
    icon: Timer
  }, {
    id: 'data',
    label: 'Data',
    icon: Database
  }, {
    id: 'about',
    label: 'About',
    icon: Info
  }];
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  return <>
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:text-foreground/80" aria-label="Settings">
          <SettingsIcon size={20} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg top-[5%] sm:top-[50%]">
        <DialogHeader className="relative text-left">
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Manage your Countdown Todo settings
          </DialogDescription>
          <Button variant="ghost" size="icon" className="absolute right-0 top-0 h-6 w-6" onClick={() => handleOpenChange(false)} aria-label="Close settings">
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>
        
        <div className="flex flex-col h-[70vh]">
          {/* Top Menu Pills */}
          <div className="border-b border-border pb-4 mb-4">
            <div className="flex flex-wrap gap-2">
              {settingsOptions.map(option => {
                const Icon = option.icon;
                return <Button key={option.id} variant={selectedSetting === option.id ? "default" : "outline"} size="sm" onClick={() => setSelectedSetting(selectedSetting === option.id ? null : option.id)} className="flex items-center gap-2">
                    <Icon size={16} />
                    {option.label}
                  </Button>;
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {selectedSetting === 'appearance' && <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-foreground">User Interface</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                       <div className="flex items-center gap-2">
                         <label className="text-sm font-medium text-foreground">Layout</label>
                       </div>
                      <p className="text-xs text-muted-foreground">Choose how to display Countdown Todo</p>
                      <ToggleGroup 
                        type="single" 
                        value={localStorage.getItem('layoutMode') || 'standard'} 
                        onValueChange={(value) => value && handleMissionsVisibilityChange(value)}
                        variant="outline"
                        className="justify-start"
                      >
                        <ToggleGroupItem value="standard" className="flex items-center gap-2">
                          Standard
                        </ToggleGroupItem>
                        <ToggleGroupItem value="missions" className="flex items-center gap-2">
                          Missions
                        </ToggleGroupItem>
                        <ToggleGroupItem value="missions-full" className="flex items-center gap-2">
                          Missions - Full
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label className="text-sm font-medium text-foreground">Show Top Cards</label>
                        <p className="text-xs text-muted-foreground">Display level tracker and mission cards</p>
                      </div>
                      <Switch 
                        checked={showTopCards} 
                        onCheckedChange={handleTopCardsVisibilityChange}
                      />
                    </div>
                    

                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label className="text-sm font-medium text-foreground">Show Inbox Mission</label>
                        <p className="text-xs text-muted-foreground">Display the auto-regenerating Inbox mission</p>
                      </div>
                      <Switch 
                        checked={showGeneralMission} 
                        onCheckedChange={handleGeneralMissionVisibilityChange}
                      />
                    </div>
                  </div>  
                </div>
              </div>}

            {selectedSetting === 'time' && (() => {
              const activeTasks = todos.filter(todo => !todo.completed);
              const hasActiveTasks = activeTasks.length > 0;
              
              return (
                <div className="space-y-4">
                  <h3 className="font-medium text-foreground">Time Settings</h3>
                  
                  {hasActiveTasks && (
                    <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">Time settings locked</p>
                        <p className="text-xs text-muted-foreground">
                          Complete all active tasks to change time settings
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4 p-1">
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${hasActiveTasks ? 'text-muted-foreground' : 'text-foreground'}`}>End of Day</label>
                      <Select 
                        value={timeSettings.endOfDayHour.toString()} 
                        onValueChange={value => handleTimeSettingChange('endOfDayHour', value)}
                        disabled={hasActiveTasks}
                      >
                        <SelectTrigger className={hasActiveTasks ? 'opacity-50 cursor-not-allowed' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {hourOptions.map(option => <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className={`text-xs ${hasActiveTasks ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>Tasks must be completed by this time</p>
                    </div>
                    
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${hasActiveTasks ? 'text-muted-foreground' : 'text-foreground'}`}>Daily Reset Time</label>
                      <Select 
                        value={timeSettings.resetHour.toString()} 
                        onValueChange={value => handleTimeSettingChange('resetHour', value)}
                        disabled={hasActiveTasks}
                      >
                        <SelectTrigger className={hasActiveTasks ? 'opacity-50 cursor-not-allowed' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {hourOptions.map(option => <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className={`text-xs ${hasActiveTasks ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>When your day starts and overdue tasks convert to overdrive</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {selectedSetting === 'durations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-foreground mb-2">Duration Settings</h3>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[0, 1, 2, 3].map((slotIndex) => (
                      <div key={slotIndex} className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          Duration Slot {slotIndex + 1}
                        </label>
                        <Select
                          value={durationSettings.primaryDurations[slotIndex].toString()}
                          onValueChange={(value) => handleDurationSettingChange(slotIndex, parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BASE_DURATION_OPTIONS.map((option) => (
                              <SelectItem 
                                key={option.value} 
                                value={option.minutes.toString()}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground pt-2">
                    Note: If a duration exceeds your end of day time, it won't appear in the modal
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-foreground">Default Duration</label>
                  </div>
                  <ToggleGroup
                    type="single"
                    value={String(durationSettings.defaultDuration)}
                    onValueChange={(value) => {
                      if (value) handleDefaultDurationChange(Number(value));
                    }}
                    variant="outline"
                    className="justify-start grid grid-cols-4 gap-2"
                  >
                    {durationSettings.primaryDurations.map((minutes) => {
                      const option = BASE_DURATION_OPTIONS.find(opt => opt.minutes === minutes);
                      return (
                        <ToggleGroupItem key={minutes} value={String(minutes)} className="h-10">
                          {option?.label || `${minutes} min`}
                        </ToggleGroupItem>
                      );
                    })}
                  </ToggleGroup>
                </div>
              </div>
            )}


            {selectedSetting === 'data' && <div className="space-y-4">
                <h3 className="font-medium text-foreground">Data Management</h3>
                <AlertDialog open={showReset} onOpenChange={setShowReset}>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full justify-start">
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Reset Data
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                        <AlertTriangle size={20} />
                        Reset Data?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete all todos, completed tasks, and your level progress. 
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleResetData} className="bg-destructive hover:bg-destructive/90">
                        Reset Everything
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>}

            {selectedSetting === 'about' && <div className="space-y-4">
                <h3 className="font-medium text-foreground">About Countdown Todo</h3>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>I originally made <a href="https://countdowntodo.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Countdown Todo</a> for myself, wanting to complete tasks faster and more often, especially my most avoided tasks. I designed it to work alongside other productivity apps, not against them.</p>
                  <p>
                    No user data is sent to any servers. All your data is stored locally.
                  </p>
                  <p>
                    <a href="https://countdowntodo.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Privacy Policy</a>
                  </p>
                  <div>
                    <p className="font-medium text-foreground mb-1">Contact:</p>
                    <p>countdowntodo@gmail.com</p>
                  </div>
                </div>
              </div>}


            {!selectedSetting && <div className="flex items-center justify-center h-full text-muted-foreground">
                <p className="text-sm">Select a setting to configure</p>
              </div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>;
}